import type { Notification } from '@nexo/types';
import { Avatar, CountBadge, EmptyState, IconButton, Popover, cn } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, CheckSquare, UserPlus, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { keys, useNotifications } from '../../lib/queries';
import {
  alertPrefs,
  notificationPermission,
  requestNotificationPermission,
  useAlertPrefs,
  type PermissionState,
} from './alerts';
import { notificationText } from './notificationText';

export function NotificationBell() {
  const { data: notifications = [] } = useNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const unread = notifications.filter((n) => !n.readAt).length;
  const prefs = useAlertPrefs();
  const { t } = useTranslation();
  const [permission, setPermission] = useState<PermissionState>('granted');
  useEffect(() => {
    void notificationPermission().then(setPermission);
  }, []);

  const markRead = (predicate: (n: Notification) => boolean) =>
    queryClient.setQueryData<Notification[]>(keys.notifications, (list) =>
      list?.map((n) =>
        predicate(n) && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n,
      ),
    );

  const readAll = useMutation({
    mutationFn: api.notifications.markAllRead,
    onMutate: () => markRead(() => true),
  });

  return (
    <Popover
      align="right"
      className="w-[22rem] max-w-[calc(100vw-2rem)]"
      trigger={({ toggle, open }) => (
        <span className="relative">
          <IconButton
            label={
              unread
                ? t('Notificaciones ({{count}} sin leer)', { count: unread })
                : t('Notificaciones')
            }
            active={open}
            onClick={toggle}
          >
            <Bell className="size-5" />
          </IconButton>
          <CountBadge
            count={unread}
            className="pointer-events-none absolute -top-0.5 -right-0.5 ring-2 ring-canvas"
          />
        </span>
      )}
    >
      {({ close }) => (
        <div>
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="flex items-center gap-1 text-sm font-semibold">
              {t('Notificaciones')}
              <IconButton
                label={prefs.sound ? t('Silenciar sonidos') : t('Activar sonidos')}
                size="sm"
                onClick={() => alertPrefs.set({ sound: !prefs.sound })}
              >
                {prefs.sound ? <Volume2 /> : <VolumeX />}
              </IconButton>
            </h2>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => readAll.mutate()}
                className="text-xs font-medium text-accent hover:underline"
              >
                {t('Marcar todo como leído')}
              </button>
            )}
          </header>
          {prefs.desktop && permission === 'default' && (
            <button
              type="button"
              onClick={() => void requestNotificationPermission().then(setPermission)}
              className="flex w-full items-center gap-2 border-b border-border bg-accent-soft/50 px-4 py-2 text-left text-xs font-medium text-accent hover:bg-accent-soft"
            >
              <BellRing className="size-4" />{' '}
              {t('Activar avisos del sistema para mensajes y llamadas')}
            </button>
          )}
          {notifications.length === 0 ? (
            <EmptyState
              icon={<Bell className="size-6" />}
              title={t('Todo al día')}
              description={t('Aquí verás asignaciones e invitaciones.')}
            />
          ) : (
            <ul className="scrollbar-thin max-h-96 overflow-y-auto py-1">
              {notifications.map((notification) => {
                const { title, href } = notificationText(notification);
                return (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!notification.readAt) {
                          markRead((n) => n.id === notification.id);
                          void api.notifications.markRead(notification.id);
                        }
                        close();
                        void navigate(href);
                      }}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-surface-2',
                        !notification.readAt && 'bg-accent-soft/40',
                      )}
                    >
                      {notification.actor ? (
                        <Avatar
                          id={notification.actor.id}
                          name={notification.actor.displayName}
                          src={notification.actor.avatarUrl}
                          size="sm"
                        />
                      ) : (
                        <span className="flex size-7 items-center justify-center rounded-lg bg-surface-2 text-muted">
                          {notification.data.type === 'task_assigned' ? (
                            <CheckSquare className="size-4" />
                          ) : (
                            <UserPlus className="size-4" />
                          )}
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm">{title}</span>
                        <span className="block text-xs text-muted">
                          {notification.actor?.displayName} ·{' '}
                          {formatRelative(notification.createdAt)}
                        </span>
                      </span>
                      {!notification.readAt && (
                        <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}
